export const DEFAULT_ACTIVE_PERIOD = 'monthly';

const DEFAULT_CALCULATOR_SETTINGS = {
	calls: {
		min: 25,
		max: 5000,
		step: 5,
		start: 25,
	},
	meetingCost: {
		min: 70,
		max: 300,
		step: 5,
		start: 70,
	},
	conversionRate: 0.25,
	lossRate: 0.46,
	multipliers: {
		daily: 1,
		monthly: 30,
		yearly: 365,
	},
};

/**
 * Read calculator settings from globally localized frontend/editor data.
 *
 * @return {Object} Raw calculator settings object.
 */
export const getLocalizedCalculatorSettings = () => {

	if ( typeof window === 'undefined' ) {

		return {};

	}

	return window.wombat?.settings?.calculator ?? window.themeVars?.calculator ?? {};

};

/**
 * Convert a raw value to a finite number or return a fallback.
 *
 * @param {*} value Raw value.
 * @param {number} fallback Fallback value.
 * @return {number} Numeric value.
 */
const toNumber = ( value, fallback ) => {

	const parsed = Number( value );

	return Number.isFinite( parsed ) ? parsed : fallback;

};

/**
 * Clamp a numeric value to an inclusive range.
 *
 * @param {number} value Value to clamp.
 * @param {number} min Minimum allowed value.
 * @param {number} max Maximum allowed value.
 * @return {number} Clamped value.
 */
const clamp = ( value, min, max ) => Math.min( max, Math.max( min, value ) );

/**
 * Normalize one slider configuration coming from PHP.
 *
 * @param {Object} rawSlider Raw slider config.
 * @param {Object} fallback Fallback slider config.
 * @return {{min:number,max:number,step:number,start:number}} Normalized config.
 */
const normalizeSlider = ( rawSlider = {}, fallback ) => {

	const min = toNumber( rawSlider.min, fallback.min );
	const max = Math.max( min, toNumber( rawSlider.max, fallback.max ) );
	const step = Math.max( 1, toNumber( rawSlider.step, fallback.step ) );
	const start = clamp( toNumber( rawSlider.start, fallback.start ), min, max );

	return {
		min,
		max,
		step,
		start,
	};

};

/**
 * Normalize calculator settings from localized PHP data.
 *
 * @param {Object} rawSettings Raw settings object.
 * @return {Object} Normalized settings.
 */
export const getCalculatorSettings = ( rawSettings = {} ) => ( {
	calls: normalizeSlider( rawSettings.calls, DEFAULT_CALCULATOR_SETTINGS.calls ),
	meetingCost: normalizeSlider( rawSettings.meetingCost, DEFAULT_CALCULATOR_SETTINGS.meetingCost ),
	conversionRate: toNumber( rawSettings.conversionRate, DEFAULT_CALCULATOR_SETTINGS.conversionRate ),
	lossRate: toNumber( rawSettings.lossRate, DEFAULT_CALCULATOR_SETTINGS.lossRate ),
	multipliers: {
		daily: Math.max( 1, toNumber( rawSettings.multipliers?.daily, DEFAULT_CALCULATOR_SETTINGS.multipliers.daily ) ),
		monthly: Math.max( 1, toNumber( rawSettings.multipliers?.monthly, DEFAULT_CALCULATOR_SETTINGS.multipliers.monthly ) ),
		yearly: Math.max( 1, toNumber( rawSettings.multipliers?.yearly, DEFAULT_CALCULATOR_SETTINGS.multipliers.yearly ) ),
	},
} );

/**
 * Format a number with Polish thousands separators.
 *
 * @param {number} value Numeric value.
 * @return {string} Formatted integer.
 */
export const formatInteger = ( value ) => new Intl.NumberFormat( 'pl-PL', {
	maximumFractionDigits: 0,
} ).format( Math.round( value ) );

/**
 * Format a value as currency with a zł suffix.
 *
 * @param {number} value Numeric value.
 * @return {string} Formatted currency string.
 */
export const formatCurrency = ( value ) => `${ formatInteger( value ) } zł`;

/**
 * Calculate the result pair for one period.
 *
 * @param {{calls:number,meetingCost:number}} values Current slider values.
 * @param {Object} settings Normalized settings.
 * @param {string} periodKey Active period key.
 * @return {{revenue:number,loss:number}} Result values.
 */
export const calculatePeriodResults = ( values, settings, periodKey ) => {

	const calls = toNumber( values.calls, settings.calls.start );
	const meetingCost = toNumber( values.meetingCost, settings.meetingCost.start );
	const multiplier = toNumber( settings.multipliers[ periodKey ], 1 );
	const baseRevenue = calls * settings.conversionRate * meetingCost;
	const revenue = baseRevenue * multiplier;

	return {
		revenue,
		loss: revenue * settings.lossRate,
	};

};

/**
 * Calculate slider progress in percent.
 *
 * @param {number} value Current value.
 * @param {number} min Minimum value.
 * @param {number} max Maximum value.
 * @return {number} Progress in the 0-100 range.
 */
export const getSliderProgress = ( value, min, max ) => {

	if ( max <= min ) {

		return 0;

	}

	return clamp( ( ( value - min ) / ( max - min ) ) * 100, 0, 100 );

};

/**
 * Bubble left position that keeps the tooltip aligned with the thumb.
 *
 * @param {number} progress Slider progress in percent.
 * @return {string} CSS calc() string.
 */
export const getSliderBubbleLeft = ( progress ) => {

	const offset = 8 - ( progress * 0.16 );

	return `calc(${ progress }% + ${ offset }px)`;

};
